import { DownOutlined } from '@ant-design/icons';
import { faPrint } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch, useSelector } from '@redux';
import crudActions from '@redux/crudActions';
import { Button, Dropdown, MenuProps, Modal, Space } from 'antd';
import { ItemType } from 'antd/es/breadcrumb/Breadcrumb';
import { MenuItemType } from 'antd/es/menu/hooks/useItems';
import CustomBreadcrumb from 'components/commons/Breadcrumb';
import React, { ReactNode, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { DynamicObjectType } from 'types/commons';

export interface IRestCreate {
  breadcrumbs?: ItemType[];
  children?: React.ReactElement;
  model: string;
  customPathEdit?: string;
  showEdit?: boolean;
  showDelete?: boolean;
  extraOptions?: MenuItemType[];
  id?: string;
  handleExtraOptions?: (e: { key: string }) => void;
  apiOptions?: DynamicObjectType;
  hasPrint?: boolean;
  extraAction?: ReactNode;
  extraBreadcrumb?: ReactNode;
  customBreadcrumb?: ReactNode;
}

export default function RestShow({
  model,
  customPathEdit,
  breadcrumbs,
  children,
  showDelete,
  showEdit,
  extraOptions,
  id,
  handleExtraOptions,
  apiOptions,
  hasPrint,
  extraAction,
  extraBreadcrumb,
  customBreadcrumb,
}: IRestCreate) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentData = useSelector<{ id: string }>(
    (state) => state[model].currentData,
  );
  const params = useParams();
  const _id = useMemo(() => id || params.id, [params.id, id]);

  const defaultBreadcrumbs = useMemo(
    () =>
      breadcrumbs || [
        {
          title: `${model}.listTitle`,
          href: `/${model}`,
        },
        {
          title: `${model}.showTitle`,
        },
      ],
    [breadcrumbs, model],
  );

  const onSelectAction = (e) => {
    if (e.key === 'edit') {
      navigate(`/${customPathEdit ? customPathEdit : model}/${_id}/edit`);
      return;
    }
    if (e.key === 'delete') {
      Modal.confirm({
        content: 'Are you sure you want to delete?',
        onOk: () => {
          dispatch(
            crudActions[model].del({
              data: currentData,
            }),
          ).then(() => {
            navigate(-1);
          });
        },
      });
      return;
    }

    handleExtraOptions?.(e);
  };

  const menu: MenuProps = useMemo(() => {
    const _options = [...(extraOptions || [])];
    if (showEdit) {
      _options.push({
        label: 'Edit',
        key: 'edit',
      });
    }
    if (showDelete) {
      _options.push({
        label: 'Delete',
        key: 'delete',
      });
    }

    return {
      items: [..._options.filter((item) => !!item.key)],
      onClick: onSelectAction,
    };
  }, [showDelete, showEdit, extraOptions, onSelectAction]);

  useEffect(() => {
    _id &&
      dispatch(
        crudActions[model].getDataById({
          data: { id: _id },
          options: {
            isRequestApi: true,
            ...apiOptions,
          },
        }),
      );
  }, [_id]);
  return (
    <div>
      <div className="d-flex space-between mb-5">
        <div>
          {customBreadcrumb ||
            (breadcrumbs && <CustomBreadcrumb items={defaultBreadcrumbs} />)}
          {extraBreadcrumb}
        </div>
        <Space>
          {extraAction}
          {!!menu?.items?.length && (
            <Dropdown trigger={['click']} menu={menu}>
              <Button className="flex items-center">
                Option <DownOutlined />
              </Button>
            </Dropdown>
          )}
          {hasPrint && (
            <Button icon={<FontAwesomeIcon icon={faPrint} />}>
              {t('button.print')}
            </Button>
          )}
        </Space>
      </div>
      {React.cloneElement(children, { record: currentData })}
    </div>
  );
}
